import { useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppFrame,
  InstallBanner,
  InstallButton,
  Sidebar,
  SidebarGroup,
  SidebarGroupHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarNav,
  Topbar,
  TopbarActions,
  TopbarBrandButton,
  TopbarIconButton,
  WorkspaceShell,
} from "taskguard-ui/app-shell";
import { BrandLogo } from "taskguard-ui/brand";
import { TODO_SCHEMA_VERSION, toVisibleTodos } from "../shared/todos";
import { useTodoStore } from "../store";

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(value: number) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function dueBetween(dueAt: number | null, start: number, end: number) {
  return dueAt != null && dueAt >= start && dueAt < end;
}

export function AppShellLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const lock = useTodoStore((state) => state.lock);
  const activeVault = useTodoStore((state) =>
    state.activeVaultId == null ? null : (state.vaults[state.activeVaultId] ?? null),
  );

  const visibleTodos = useMemo(
    () =>
      activeVault == null
        ? []
        : toVisibleTodos({
            schemaVersion: TODO_SCHEMA_VERSION,
            deviceId: "",
            lastMutatedAt: activeVault.lastMutatedAt,
            todos: activeVault.todos,
          }),
    [activeVault],
  );

  const openTodoCount = visibleTodos.filter((todo) => !todo.completed).length;
  const scheduledTodoCount = visibleTodos.filter((todo) => todo.dueAt != null).length;
  const today = startOfDay(Date.now());
  const tomorrow = today + DAY_MS;
  const nextWeek = today + 7 * DAY_MS;
  const nextMonth = new Date(today);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const todayCount = visibleTodos.filter((todo) => dueBetween(todo.dueAt, today, tomorrow)).length;
  const tomorrowCount = visibleTodos.filter((todo) => dueBetween(todo.dueAt, tomorrow, tomorrow + DAY_MS)).length;
  const weekCount = visibleTodos.filter((todo) => dueBetween(todo.dueAt, today, nextWeek)).length;
  const monthCount = visibleTodos.filter((todo) => dueBetween(todo.dueAt, today, nextMonth.getTime())).length;
  const isCalendarRoute = location.pathname.startsWith("/calendar");
  const isVaultDetailRoute = location.pathname.startsWith("/vaults/");
  const activeVaultPath = activeVault == null ? "/vaults" : `/vaults/${activeVault.id}`;

  return (
    <AppFrame>
      <Topbar>
        <TopbarBrandButton onClick={() => navigate("/vaults")} type="button">
          <BrandLogo />
        </TopbarBrandButton>

        <TopbarActions aria-label="Global actions">
          <TopbarIconButton aria-label="Vaults" onClick={() => navigate("/vaults")} type="button">
            ▣
          </TopbarIconButton>
          <TopbarIconButton aria-label="Search" onClick={() => navigate(activeVaultPath)} type="button">
            ⌕
          </TopbarIconButton>
          <TopbarIconButton
            aria-label="Notifications"
            badge={scheduledTodoCount}
            onClick={() => navigate("/calendar")}
            type="button"
          >
            ●
          </TopbarIconButton>
          <TopbarIconButton aria-label="Lock TaskGuard" onClick={lock} type="button">
            ♟
          </TopbarIconButton>
        </TopbarActions>
      </Topbar>

      <InstallBanner>
        <span>Please install the TaskGuard app to get offline access and local notifications.</span>
        <InstallButton type="button">
          <span aria-hidden="true">⌄</span>
          Install
        </InstallButton>
      </InstallBanner>

      <Sidebar>
        <SidebarNav aria-label="Primary">
          <SidebarGroup>
            <SidebarGroupHeader icon="☷" onClick={() => navigate(activeVaultPath)}>
              Tasks
            </SidebarGroupHeader>
            <SidebarMenu>
              <SidebarMenuButton color="#d99a33" count={openTodoCount} icon="■" onClick={() => navigate(activeVaultPath)}>
                All
              </SidebarMenuButton>
              <SidebarMenuButton
                active={isVaultDetailRoute}
                color="#35b7d2"
                count={openTodoCount}
                icon="▰"
                onClick={() => navigate(activeVaultPath)}
              >
                Inbox
              </SidebarMenuButton>
              <SidebarMenuButton color="#8d56b2" icon="▣" onClick={() => navigate("/vaults")}>
                Projects
              </SidebarMenuButton>
              <SidebarMenuButton color="#b14d91" icon="◆" onClick={() => navigate("/vaults")}>
                Labels
              </SidebarMenuButton>
              <SidebarMenuButton color="#d6b82c" icon="╱" onClick={() => navigate(activeVaultPath)}>
                Cleanup
              </SidebarMenuButton>
              <SidebarMenuButton color="#4a91c9" icon="◔" onClick={() => navigate(activeVaultPath)}>
                Insights
              </SidebarMenuButton>
              <SidebarMenuButton color="#2b7197" icon="⟳" onClick={() => navigate(activeVaultPath)}>
                Habits
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupHeader icon="▣" onClick={() => navigate("/calendar")}>
              Calendar
            </SidebarGroupHeader>
            <SidebarMenu>
              <SidebarMenuButton color="#56b4c1" count={todayCount} icon="✺" onClick={() => navigate("/calendar?scope=day")}>
                Today
              </SidebarMenuButton>
              <SidebarMenuButton
                color="#b36a2e"
                count={tomorrowCount}
                icon="▪"
                onClick={() => navigate("/calendar?scope=day")}
              >
                Tomorrow
              </SidebarMenuButton>
              <SidebarMenuButton color="#239ab0" count={weekCount} icon="▣" onClick={() => navigate("/calendar?scope=week")}>
                Week
              </SidebarMenuButton>
              <SidebarMenuButton color="#f1a627" count={monthCount} icon="▦" onClick={() => navigate("/calendar?scope=month")}>
                Month
              </SidebarMenuButton>
              <SidebarMenuButton
                active={isCalendarRoute}
                color="#4688bf"
                count={scheduledTodoCount}
                icon="■"
                onClick={() => navigate("/calendar")}
              >
                Planned
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroup>
        </SidebarNav>
      </Sidebar>

      <WorkspaceShell>
        <Outlet />
      </WorkspaceShell>
    </AppFrame>
  );
}
